import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { ProductOptionGroup } from '../types';
import { Sheet, SheetHeader, SheetTitle, SheetBody, SheetActions } from './ui';
import './CustomizationModal.css';

interface CustomizationModalProps {
  productName: string;
  basePrice: number;
  optionGroups: ProductOptionGroup[];
  initialCustomization?: Record<string, string | string[]> | null;
  onSave: (customization: Record<string, string | string[]>, totalPrice: number) => void;
  onClose: () => void;
}

function computePrice(basePrice: number, groups: ProductOptionGroup[], customization: Record<string, string | string[]>): number {
  let total = basePrice;
  for (const group of groups) {
    const selected = customization[group.label];
    if (!selected) continue;
    const vals = Array.isArray(selected) ? selected : [selected];
    for (const v of vals) {
      const choice = group.choices.find((c) => c.label === v);
      if (choice?.priceModifier != null) total += Number(choice.priceModifier);
    }
  }
  return total;
}

export default function CustomizationModal({
  productName,
  basePrice,
  optionGroups,
  initialCustomization = null,
  onSave,
  onClose,
}: CustomizationModalProps) {
  const { t } = useTranslation();
  const [customization, setCustomization] = useState<Record<string, string | string[]>>({});

  useEffect(() => {
    if (initialCustomization) setCustomization({ ...initialCustomization });
  }, [initialCustomization]);

  const toggleSingle = (groupLabel: string, choiceLabel: string) => {
    setCustomization((prev) => ({ ...prev, [groupLabel]: choiceLabel }));
  };

  const toggleMultiple = (groupLabel: string, choiceLabel: string) => {
    setCustomization((prev) => {
      const current = (prev[groupLabel] as string[]) || [];
      const next = current.includes(choiceLabel)
        ? current.filter((c) => c !== choiceLabel)
        : [...current, choiceLabel];
      return { ...prev, [groupLabel]: next };
    });
  };

  const totalPrice = computePrice(basePrice, optionGroups, customization);

  const handleSave = () => {
    onSave(customization, totalPrice);
    onClose();
  };

  return (
    <Sheet open={true} onClose={onClose} className="customization-sheet">
      <SheetHeader>
        <SheetTitle>{t('orderStatus.customize')} {productName}</SheetTitle>
      </SheetHeader>
      <SheetBody>
        {optionGroups.map((group, gi) => (
          <div key={group.label || `group-${gi}`} className="customization-group">
            <label className="customization-group-label">{group.label || t('modals.optionGroup')}</label>
            {group.type === 'single' ? (
              <div className="customization-choices single">
                {group.choices.map((choice, ci) => (
                  <label key={choice.label ? `${group.label}-${choice.label}` : `choice-${ci}`} className="customization-choice">
                    <input
                      type="radio"
                      name={`custom-group-${gi}`}
                      checked={(customization[group.label] as string) === choice.label}
                      onChange={() => toggleSingle(group.label, choice.label)}
                    />
                    <span className="customization-choice-text">{choice.label}</span>
                    {choice.priceModifier !== 0 && (
                      <span className={`price-mod ${choice.priceModifier > 0 ? 'price-mod-plus' : 'price-mod-minus'}`}>
                        {choice.priceModifier > 0 ? '+' : '−'}₸{Math.abs(choice.priceModifier).toFixed(2)}
                      </span>
                    )}
                  </label>
                ))}
              </div>
            ) : (
              <div className="customization-choices multiple">
                {group.choices.map((choice, ci) => (
                  <label key={choice.label ? `${group.label}-${choice.label}` : `choice-${ci}`} className="customization-choice">
                    <input
                      type="checkbox"
                      checked={((customization[group.label] as string[]) || []).includes(choice.label)}
                      onChange={() => toggleMultiple(group.label, choice.label)}
                    />
                    <span className="customization-choice-text">{choice.label}</span>
                    {choice.priceModifier !== 0 && (
                      <span className={`price-mod ${choice.priceModifier > 0 ? 'price-mod-plus' : 'price-mod-minus'}`}>
                        {choice.priceModifier > 0 ? '+' : '−'}₸{Math.abs(choice.priceModifier).toFixed(2)}
                      </span>
                    )}
                  </label>
                ))}
              </div>
            )}
          </div>
        ))}
      </SheetBody>
      <SheetActions>
        <span className="customization-total">₸{totalPrice.toFixed(2)}</span>
        <button type="button" className="customization-save-btn" onClick={handleSave}>
          {t('common.save')}
        </button>
      </SheetActions>
    </Sheet>
  );
}
